import { CmsSectionShell } from './CmsSectionShell.tsx';
import { themeOf, EYEBROW, BUTTON_FILL } from './cmsTheme.ts';
import type { NewsletterSection } from '../../lib/types.ts';

/**
 * Newsletter signup block. Heading and intro are configurable. The newsletter is handled by an
 * external provider, so instead of a form we render a single call-to-action button that opens the
 * provider's signup page in a new tab. No email is collected here (nothing would receive it) and
 * no personal data is put in a URL. The signup URL is authored per-section in the CMS so the
 * provider can change without a code change; when it is unset the CTA is omitted (heading/intro
 * still show).
 *
 * v2 shape: one card, copy on the left and the CTA on the right (stacked on mobile). The button
 * follows the section's filled-control color, which is what fixes the old coral-on-coral case —
 * a coral section used to render a coral button on a coral background.
 */
export function CmsNewsletterSection({
  theme,
  guitar,
  heading,
  intro,
  buttonLabel = 'Tilmeld nyhedsbrev',
  signupUrl,
}: NewsletterSection) {
  const t = themeOf(theme);

  return (
    <CmsSectionShell theme={theme} guitar={guitar}>
      <div className={`rounded-card p-6 md:p-8 ${t.card.bg} ${t.card.text} flex flex-col gap-6 md:flex-row md:items-center md:justify-between md:gap-10`}>
        <div className="md:flex-1">
          <div className={`${EYEBROW} ${t.eyebrow} mb-2`}>Nyhedsbrev</div>
          <h2 className="font-display text-3xl md:text-[40px] leading-none tracking-[-0.02em]">
            {heading}
          </h2>
          {intro && <p className={`mt-3 max-w-xl leading-relaxed ${t.card.muted}`}>{intro}</p>}
        </div>

        {signupUrl && (
          <a
            href={signupUrl}
            target="_blank"
            rel="noopener noreferrer"
            className={`inline-flex shrink-0 items-center gap-2 rounded-full px-5 md:px-6 py-2.5 md:py-3 font-medium text-sm md:text-base transition-colors ${BUTTON_FILL[t.button]}`}
          >
            {buttonLabel} →
          </a>
        )}
      </div>
    </CmsSectionShell>
  );
}
